import { UseCase } from '@/types/use-case';

// Sample API response for venue locations (Central Park, NYC and Bryant Park, NYC)
const sampleApiResponse = {
  data: {
    timelines: [{
      timestep: "1h",
      intervals: [
        {
          time: "2025-01-06T14:00:00Z",
          values: {
            temperature: 16,
            humidity: 86,
            precipitationProbability: 0,
            rainIntensity: 0,
            windSpeed: 4.1,
            windDirection: 83,
            windGust: 6.8,
            weatherCode: 1001,
            visibility: 13.31,
            uvIndex: 0,
            cloudCover: 100
          }
        },
        {
          time: "2025-01-06T15:00:00Z",
          values: {
            temperature: 17,
            humidity: 69,
            precipitationProbability: 0,
            rainIntensity: 0,
            windSpeed: 3.5,
            windDirection: 68,
            windGust: 6.9,
            weatherCode: 1001,
            visibility: 13.14,
            uvIndex: 1,
            cloudCover: 100
          }
        },
        {
          time: "2025-01-06T16:00:00Z",
          values: {
            temperature: 19,
            humidity: 68,
            precipitationProbability: 0,
            rainIntensity: 0,
            windSpeed: 3.4,
            windDirection: 76,
            windGust: 7.0,
            weatherCode: 1001,
            visibility: 12.97,
            uvIndex: 2,
            cloudCover: 100
          }
        },
        {
          time: "2025-01-06T17:00:00Z",
          values: {
            temperature: 20,
            humidity: 65,
            precipitationProbability: 15,
            rainIntensity: 0.2,
            windSpeed: 4.2,
            windDirection: 78,
            windGust: 8.1,
            weatherCode: 4000,
            visibility: 11.5,
            uvIndex: 1,
            cloudCover: 95
          }
        },
        {
          time: "2025-01-06T18:00:00Z",
          values: {
            temperature: 21,
            humidity: 62,
            precipitationProbability: 75,
            rainIntensity: 1.8,
            windSpeed: 5.1,
            windDirection: 82,
            windGust: 9.2,
            weatherCode: 4001,
            visibility: 8.3,
            uvIndex: 0,
            cloudCover: 98
          }
        }
      ]
    }],
    location: {
      lat: 21.3099,
      lon: -157.8581
    }
  }
};

export const eventPlanningVenuesUseCase: UseCase = {
  id: 'event-planning-venues',
  title: 'Event Planning & Venue Management Dashboard',
  industry: 'Event Management',
  complexity: 'Intermediate',
  description: 'Smart event planning dashboard that combines Tomorrow.io weather forecasts with interactive map visualizations to help event planners make data-driven decisions about outdoor events, venue selection, and contingency planning.',
  problemStatement: 'Event planners managing multiple outdoor venues (weddings, concerts, festivals, corporate events) need to make critical decisions about event scheduling, venue selection, and backup plans based on weather conditions. Traditional weather apps don\'t provide the location-specific precision needed for multi-venue event management.',
  solutionOverview: 'This implementation demonstrates how to build an interactive dashboard that displays weather data for multiple event venues on an interactive map, provides venue-specific weather suitability scores, and helps planners make informed decisions about event logistics and contingency planning.',
  
  apiRequest: {
    endpoint: '/weather/forecast',
    method: 'GET',
    parameters: {
      location: '21.3099,-157.8581',
      apikey: 'YOUR_API_KEY'
    }
  },
  
  apiResponse: {
    status: 200,
    data: sampleApiResponse.data,
    timestamp: new Date().toISOString()
  },
  
  implementationCode: {
    language: 'javascript',
    code: `// Initialize Leaflet map with OpenStreetMap tiles
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const map = L.map('map-container', {
  center: [20.5, -156.5], // Hawaii center coordinates [lat, lng]
  zoom: 8,
  zoomControl: true,
  scrollWheelZoom: true
});

// Add OpenStreetMap tile layer (no API key required!)
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
  attribution: '© OpenStreetMap contributors',
  maxZoom: 19
}).addTo(map);

// Event venues across Hawaii's diverse microclimates
const venues = [
  { 
    id: 'honolulu-beach', 
    name: 'Honolulu Beach Resort', 
    lat: 21.3099, 
    lon: -157.8581,
    eventType: 'Wedding Reception',
    capacity: 200
  },
  { 
    id: 'hilo-gardens', 
    name: 'Hilo Tropical Gardens', 
    lat: 19.7297, 
    lon: -155.0900,
    eventType: 'Corporate Retreat',
    capacity: 300
  },
  { 
    id: 'kona-coast', 
    name: 'Kona Coast Venue', 
    lat: 19.8968, 
    lon: -155.5828,
    eventType: 'Music Festival',
    capacity: 1000
  },
  { 
    id: 'mauna-kea', 
    name: 'Mauna Kea Observatory', 
    lat: 19.8207, 
    lon: -155.4680,
    eventType: 'Stargazing Event',
    capacity: 150
  }
];

// Fetch weather data for all venues
async function updateVenueWeather(apiKey) {
  console.log('Fetching weather data for', venues.length, 'venues...');
  
  const weatherPromises = venues.map(async (venue) => {
    try {
      const url = \`https://api.tomorrow.io/v4/weather/forecast?location=\${venue.lat},\${venue.lon}&apikey=\${apiKey}&timesteps=1h&fields=temperature,humidity,precipitationProbability,windSpeed,visibility\`;
      
      console.log(\`Fetching weather for \${venue.name} at \${venue.lat},\${venue.lon}\`);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(\`API error: \${response.status}\`);
      }
      
      const data = await response.json();
      const currentWeather = data.timelines[0].intervals[0].values;
      
      return { 
        ...venue, 
        weather: currentWeather,
        score: calculateEventScore(currentWeather)
      };
      
    } catch (error) {
      console.error(\`Failed to fetch weather for \${venue.name}:\`, error);
      return { 
        ...venue, 
        weather: null, 
        score: 0 
      };
    }
  });
  
  const venuesWithWeather = await Promise.all(weatherPromises);
  return venuesWithWeather;
}

// Calculate event suitability score based on weather conditions
function calculateEventScore(weather) {
  if (!weather) return 0;
  
  let score = 100;
  
  // Temperature comfort (optimal: 18-24°C)
  if (weather.temperature < 10 || weather.temperature > 30) score -= 30;
  else if (weather.temperature < 15 || weather.temperature > 27) score -= 15;
  
  // Rain probability impact
  if (weather.precipitationProbability > 70) score -= 40;
  else if (weather.precipitationProbability > 30) score -= 20;
  
  // Wind conditions (high winds problematic for tents, stages)
  if (weather.windSpeed > 10) score -= 25;
  else if (weather.windSpeed > 6) score -= 10;
  
  // Visibility for photography/videography
  if (weather.visibility < 5) score -= 15;
  else if (weather.visibility < 10) score -= 5;
  
  return Math.max(0, Math.round(score));
}

// Add weather-based markers to map
async function displayVenuesOnMap(apiKey) {
  const venuesWithWeather = await updateVenueWeather(apiKey);
  
  venuesWithWeather.forEach(venue => {
    const score = venue.score;
    const color = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';
    
    // Create custom marker element
    const markerElement = document.createElement('div');
    markerElement.style.cssText = \`
      width: 30px; height: 30px;
      background: \${color};
      border: 3px solid white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      color: white;
      font-size: 10px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.4);
    \`;
    markerElement.textContent = score.toString();
    
    // Create custom Leaflet icon
    const customIcon = L.divIcon({
      html: markerElement.outerHTML,
      className: 'custom-marker',
      iconSize: [30, 30],
      iconAnchor: [15, 15]
    });
    
    // Create detailed popup content
    const popupContent = \`
      <div style="padding: 12px; font-family: sans-serif; max-width: 250px;">
        <h3 style="margin: 0 0 8px 0; color: #1f2937;">\${venue.name}</h3>
        <div style="margin-bottom: 8px; padding: 6px; background: \${score >= 80 ? '#ecfdf5' : score >= 60 ? '#fefce8' : '#fef2f2'}; border-radius: 4px;">
          <strong>Suitability Score: \${score}/100</strong>
        </div>
        \${venue.weather ? \`
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px;">
            <div><strong>🌡️ Temperature:</strong><br/>\${venue.weather.temperature}°C</div>
            <div><strong>🌧️ Rain Chance:</strong><br/>\${venue.weather.precipitationProbability}%</div>
            <div><strong>💨 Wind Speed:</strong><br/>\${venue.weather.windSpeed} m/s</div>
            <div><strong>👁️ Visibility:</strong><br/>\${venue.weather.visibility} km</div>
          </div>
        \` : '<div style="color: red;">Weather data unavailable</div>'}
        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #6b7280;">
          <strong>Event Type:</strong> \${venue.eventType}<br/>
          <strong>Capacity:</strong> \${venue.capacity} guests
        </div>
      </div>
    \`;
    
    // Add marker to map
    L.marker([venue.lat, venue.lon], { icon: customIcon })
      .bindPopup(popupContent)
      .addTo(map);
  });
  
  console.log('All venue markers added to map');
  return venuesWithWeather;
}

// Initialize the venue weather dashboard
displayVenuesOnMap('YOUR_API_KEY').then(venues => {
  console.log('Venue weather dashboard initialized with', venues.length, 'locations');
});`
  },
  
  chartConfig: {
    type: 'line',
    datasets: [
      {
        label: 'Event Suitability Score',
        data: sampleApiResponse.data.timelines[0].intervals.map((interval, index) => ({
          x: interval.time,
          y: 100 - interval.values.precipitationProbability * 0.4 - 
              Math.max(0, Math.abs(interval.values.temperature - 20) * 2) -
              Math.max(0, (interval.values.windSpeed - 5) * 3)
        })),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)'
      },
      {
        label: 'Temperature (°C)',
        data: sampleApiResponse.data.timelines[0].intervals.map(interval => ({
          x: interval.time,
          y: interval.values.temperature
        })),
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)'
      }
    ],
    options: {
      responsive: true,
      plugins: {
        title: {
          display: true,
          text: 'Event Venue Weather Analysis'
        }
      },
      scales: {
        x: {
          type: 'time',
          time: {
            unit: 'hour'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Score / Temperature'
          }
        }
      }
    }
  },
  
  businessMetrics: [
    {
      title: 'Risk Mitigation',
      description: 'Weather-based alerts prevent costly event cancellations',
      impact: 'Significant reduction in weather-related event issues'
    },
    {
      title: 'Venue Optimization',
      description: 'Data-driven venue selection based on weather patterns',
      impact: 'Improved guest satisfaction and event success rates'
    },
    {
      title: 'Equipment Planning',
      description: 'Precise weather data for equipment and logistics decisions',
      impact: 'Optimized setup costs and resource allocation'
    },
    {
      title: 'Guest Experience',
      description: 'Proactive communication about weather conditions',
      impact: 'Enhanced attendee preparation and comfort'
    }
  ],
  
  tags: [
    'Leaflet.js',
    'OpenStreetMap',
    'Event Planning',
    'Weather Visualization',
    'Venue Management',
    'JavaScript',
    'Real-time Data',
    'Risk Assessment',
    'Geographic Information Systems'
  ],
  
  estimatedROI: 'Reduced weather-related costs and improved planning efficiency',
  timeToImplement: 'Implementation complexity: Intermediate'
}; 